import React, {useContext, useEffect, useState} from "react";
import {
    Card,
    Layout,
    Page,
    Popover,
    ActionList,
    SkeletonBodyText,
    DataTable,
    ButtonGroup,
    Button,
    TextStyle, Banner, List
} from "@shopify/polaris";
import {useHistory, useParams} from "react-router-dom";
import WhatsappChatWindow from "../components/WhatsappChatWindow";
import axios from "../axios";
import {ShopContext} from "../App";
import _ from "lodash";
import {getShortcodeLists, parseShortcodes} from "../utils";
import {isMobile} from "@shopify/app-bridge-utils";
import Br from "../components/Br";

const SendMessage = () => {
    const router = useHistory();
    const config = useContext(ShopContext);
    const {id, type} = useParams();
    const [loading, setLoading] = useState(false);
    const [isMessageSending, setIsMessageSending] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [previewMessage, setPreviewMessage] = useState('');
    const [checkout, setCheckout] = useState({});

    useEffect(() => {
        setLoading(true);
        axios.get(`/checkouts/send-message/${id}/${type}`, {
            "headers": {
                shopOrigin: config.shopOrigin,
                xShopHost: config.host,
            }
        }).then(async (response) => {
            setLoading(false);
            let responseCheckout = _.get(response, 'data.checkout', {});
            if (response.success && responseCheckout) {
                setCheckout(responseCheckout)
                let responseTemplates = _.get(response, 'data.templates', []);
                let templates = _processResponseTemplates(responseTemplates, responseCheckout);
                setTemplates(templates);
            }
        }).catch((e) => {
            setLoading(false);
        });
        //eslint-disable-next-line
    }, [config, id]);

    const markAsMessageSent = (id, message, phone) => {
        setIsMessageSending(true);
        axios.put(`/checkouts/message-sent/${id}`, {}, {
            "headers": {
                shopOrigin: config.shopOrigin,
                xShopHost: config.host,
            }
        }).then((response) => {
            if (response.success) {
                let link = `https://web.whatsapp.com/send`;
                if (isMobile()) {
                    link = `https://api.whatsapp.com/send`;
                }
                let trimmedPhoneNumber = phone.replace(/[^\d]/, "");
                let whatsAppChatUrl = `${link}/?phone=${trimmedPhoneNumber}&text=${encodeURI(message)}`;
                window.open(whatsAppChatUrl, "_blank");
            }
            setIsMessageSending(false);
        }).catch((e) => {
            setIsMessageSending(false);
        });
    }

    const TemplateActions = ({onViewPreview, onSendMessage, onDeleteTemplate, onEditTemplate, disabled}) => {
        const [active, setActive] = useState(false);
        const toggleActive = React.useCallback(() => setActive((active) => !active), []);
        const activator = (
            <Button onClick={toggleActive} disclosure>
                More
            </Button>
        );
        return <div style={{}}>
            <ButtonGroup segmented>
                <Button onClick={onSendMessage} primary>Send </Button>
                <Button onClick={onViewPreview}>Preview</Button>
                <Popover active={active} activator={activator} onClose={toggleActive}>
                    <ActionList
                        sections={[
                            {
                                items: [
                                    {
                                        content: 'Send message',
                                        onAction: onSendMessage,
                                        disabled: disabled,
                                    }, {
                                        content: 'View preview',
                                        onAction: onViewPreview,
                                        disabled: disabled,
                                    },
                                    {
                                        content: 'Edit template',
                                        onAction: onEditTemplate
                                    },
                                    {
                                        content: 'Delete template',
                                        onAction: onDeleteTemplate,
                                        disabled: disabled,
                                    },
                                ],
                            },
                        ]}
                    />
                </Popover>
            </ButtonGroup>
        </div>
    }

    /**
     * process the response to generate the table columns data
     * @param templates
     * @param checkout
     * @returns {[]}
     */
    const _processResponseTemplates = (templates, checkout) => {
        let processedTemplates = [];
        if (templates.length > 0) {
            templates.forEach((template) => {
                let newTemplate = [];
                const message = _.get(template, 'message_text', "");
                const shortcodes = getShortcodeLists(checkout, type);
                const parsedMessageTemplate = parseShortcodes(message, shortcodes);
                newTemplate.push(<>{template.message_text}</>);
                newTemplate.push(<TemplateActions
                    disabled={isMessageSending}
                    onViewPreview={() => {
                        setPreviewMessage(parsedMessageTemplate);
                    }}
                    onEditTemplate={() => {
                        router.push(`/${type === 'pre_order' ? 'checkouts' : 'orders'}/templates/${template.id}/edit/${type}`);
                    }}
                    onDeleteTemplate={() => {
                        deleteTemplate(template.id)
                    }}
                    onSendMessage={() => {
                        let checkoutID = _.get(checkout, 'id', 0);
                        let phone = _.get(checkout, 'phone_number', null);
                        markAsMessageSent(checkoutID, parsedMessageTemplate, phone);
                    }}/>)
                processedTemplates.push(newTemplate);
            });
        }
        return processedTemplates;
    }
    /**
     * get the skeleton content for page data table
     * @returns {[]}
     */
    const getTableSkeletonContent = () => {
        let skeleton = [];
        for (let i = 0; i < 4; i++) {
            skeleton.push([
                <SkeletonBodyText lines={1}/>,
                <SkeletonBodyText lines={1}/>,
            ]);
        }
        return skeleton;
    }
    /**
     * Delete the template by ID
     * @param templateId
     */
    const deleteTemplate = (templateId) => {
        setLoading(true);
        axios.delete(`/templates/${templateId}?page=1&type=${type}`, {
            "headers": {
                shopOrigin: config.shopOrigin,
                xShopHost: config.host,
            }
        }).then((response) => {
            setLoading(false);
            let responseTemplates = _.get(response, 'data.data', []);
            let templates = _processResponseTemplates(responseTemplates, checkout);
            setTemplates(templates);
        }).catch((error) => {
            setLoading(false);
        });
    };
    let skeleton = getTableSkeletonContent();
    return (
        <>
            <Page
                breadcrumbs={[{
                    accessibilityLabel: 'Checkouts', content: 'Checkouts', onAction: () => {
                        router.push(`/${type === 'pre_order' ? 'checkouts' : 'orders'}`);
                    }
                }]}
                fullWidth={true}
                title="Send message"
                separator
            >
                <div>
                    <p>Choose one of the following messages to send a recovery message via WhatsApp</p>
                    <Br height={10}/>
                    <p><TextStyle variation="strong">NOTE: </TextStyle> <TextStyle variation="subdued"> If you are on a
                        desktop, this will open the WhatsApp Web. If you are in a mobile, then this will open your
                        WhatsApp
                        app to send the message</TextStyle></p>
                    <Br height={20}/>
                </div>
                <Layout>
                    <Layout.Section>
                        <Card>
                            <DataTable
                                columnContentTypes={[
                                    'text',
                                    'text'
                                ]}
                                headings={[
                                    'Message',
                                    'Action'
                                ]}
                                rows={(loading) ? skeleton : templates}
                                footerContent={(loading) ? `Loading templates! please wait...` : `Showing ${templates.length} message templates`}
                            />
                        </Card>
                        <Br height="30"/>
                        <div style={{textAlign: "center"}}><Button primary onClick={() => {
                            router.push(`/${type === 'pre_order' ? 'checkouts' : 'orders'}/templates/0/create/${type}`);
                        }}>Create new template</Button></div>
                    </Layout.Section>
                    <Layout.Section secondary>
                        <Card title="Preview" sectioned>
                            <WhatsappChatWindow message={previewMessage}/>
                        </Card>
                    </Layout.Section>
                </Layout>
            </Page>
            <div style={{width: "95%", margin: "auto", marginTop: "20px"}}>
                <Banner
                    title="Before getting started:"
                    status="info"
                >
                    <p>
                        Please set up the WhatsApp Web (if you are using in a computer / pc / laptop). <a
                        target="_blank"
                        href="https://www.howtogeek.com/676621/how-to-use-whatsapp-on-your-computer-and-web/">Here
                        is a step-by-step tutorial </a>
                        WhatsApp web is a feature offered by WhatsApp itself.
                    </p>
                    <Br height={10}/>
                    <div><TextStyle variation="strong">Note: </TextStyle> If you opened this screen in a mobile, you
                        can send the message via your WhatsApp mobile app.
                    </div>
                    <Br height={10}/>
                    <div><TextStyle variation="strong">IMPORTANT: </TextStyle> If a phone number is not on whatsapp, it
                        will show an alert that the phone number is invalid when sending the message.
                    </div>
                    <Br height={10}/>
                    <div><TextStyle variation="strong">Best Practices: </TextStyle></div>
                    <Br height={10}/>
                    <List type="number">
                        <List.Item>Keep your messaging to 20 to 30 per hour.</List.Item>
                        <List.Item>If you are starting with a new phone number, please wait for a few days after
                            registering for a WhatsApp account.</List.Item>
                        <List.Item>Sending hundreds of messages might be consider spammy and WhatsApp may ban your
                            account</List.Item>
                    </List>
                </Banner>
            </div>
        </>
    );
}
export default SendMessage;
